// Password strength calculator
export interface PasswordStrength {
  score: number // 0-4
  feedback: string[]
  color: 'red' | 'yellow' | 'green'
  label: string
}

const commonPasswords = [
  'password', '12345678', 'qwerty', 'abc123', 'letmein', 'welcome', 
  'monkey', '1234567890', 'password123', 'admin', 'root'
]

export function calculatePasswordStrength(password: string, email?: string): PasswordStrength {
  if (!password) {
    return { score: 0, feedback: [], color: 'red', label: 'No password' }
  }

  let score = 0
  const feedback: string[] = []

  // Length check
  if (password.length >= 8) {
    score += 1
  } else {
    feedback.push('At least 8 characters')
  }

  if (password.length >= 12) {
    score += 1
  }

  // Complexity checks
  const hasUpperCase = /[A-Z]/.test(password)
  const hasLowerCase = /[a-z]/.test(password)
  const hasNumbers = /\d/.test(password)
  const hasSpecialChars = /[!@#$%^&*(),.?":{}|<>]/.test(password)

  if (hasUpperCase && hasLowerCase) {
    score += 1
  } else {
    feedback.push('Mix of uppercase and lowercase')
  }

  if (hasNumbers) {
    score += 0.5
  } else {
    feedback.push('Include numbers')
  }

  if (hasSpecialChars) {
    score += 0.5
  } else {
    feedback.push('Include special characters')
  }

  // Common password check
  const lowerPassword = password.toLowerCase()
  if (commonPasswords.some(common => lowerPassword.includes(common))) {
    score -= 2
    feedback.push('Avoid common passwords')
  }

  // Email similarity check
  if (email && password.toLowerCase().includes(email.split('@')[0].toLowerCase())) {
    score -= 1
    feedback.push('Don\'t use your email in password')
  }

  // Normalize score
  const finalScore = Math.max(0, Math.min(4, Math.floor(score)))

  // Determine color and label
  let color: 'red' | 'yellow' | 'green'
  let label: string

  if (finalScore === 0 || finalScore === 1) {
    color = 'red'
    label = 'Weak'
  } else if (finalScore === 2) {
    color = 'yellow'
    label = 'Medium'
  } else if (finalScore === 3) {
    color = 'yellow'
    label = 'Good'
  } else {
    color = 'green'
    label = 'Strong'
  }

  return {
    score: finalScore,
    feedback: feedback.slice(0, 2), // Show max 2 suggestions
    color,
    label
  }
}

